import { Fragment, useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Dropdown,
  Nav,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Form from "./form";
import SelectMultiple from "./selectMultiple";

const BootstrapTable = ({
  getAllRecord = () => {},
  deleteRecord = null,
  updateRecord = null,
  createRecord = null,
  title = "",
  fields = [],
  menuOptions = [],
  showActions = true,
  searchAble = false,
}) => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const requestIdRef = useRef(0);

  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({});

  // Debounce: wait 400ms after user stops typing before fetching
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getAndSetData = async (page = currentPage) => {
    const requestId = ++requestIdRef.current;
    try {
      const result = await getAllRecord(page, recordsPerPage, debouncedSearch);
      // Discard stale responses from previous searches / page changes
      if (requestId !== requestIdRef.current) return;
      setData(result.data);
      setTotalRecords(result.total);
      setCurrentPage(page);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to fetch records:", error);
      swal("Failed to fetch records!", { icon: "error" });
    }
  };

  useEffect(() => {
    getAndSetData(1);
  }, [debouncedSearch]);

  const handleDelete = async (id) => {
    const delStatus = await deleteRecord(id);
    if (delStatus == 204) {
      swal("Record deleted successfully!", { icon: "success" });
      getAndSetData(currentPage);
    } else {
      swal("Something went wrong!", { icon: "warning" });
    }
  };

  const handleDeleteConfirm = (data) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you cannot recover this data!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        handleDelete(data?.id);
      }
    });
  };

  const handleFormSubmit = async (submitFormData) => {
    if (submitFormData?.id) {
      console.log("RUNNER SUBMIT FORM DATA", submitFormData);
      await updateRecord(submitFormData);
    } else {
      await createRecord(submitFormData);
    }
    setOpenModal(false);
    getAndSetData(1);
  };

  const handlePageChange = (pageNumber) => {
    getAndSetData(pageNumber);
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const items = [];
    const visiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let endPage = startPage + visiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - visiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(
          <span key="start-ellipsis" className="px-2 text-primary">
            ...
          </span>
        );
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="end-ellipsis" className="px-2 text-primary">
            ...
          </span>
        );
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mt-4 pagination-gutter pagination-primary no-bg">
        <li className="page-item page-indicator">
          <Link
            className="page-link"
            to="#"
            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          >
            <i className="la la-angle-left" />
          </Link>
        </li>
        {items}
        <li className="page-item page-indicator">
          <Link
            className="page-link"
            to="#"
            onClick={() =>
              currentPage < totalPages && handlePageChange(currentPage + 1)
            }
          >
            <i className="la la-angle-right" />
          </Link>
        </li>
      </Pagination>
    );
  };

  const tableFields = fields?.filter((f) => f.isTable);

  return (
    <Fragment>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <Card.Title>{title}</Card.Title>
              {searchAble && (
                <input
                  type="text"
                  className="form-control w-100"
                  style={{ marginLeft: "2rem", marginRight: "2rem" }}
                  placeholder="Search by name"
                  value={searchQuery}
                  onChangeCapture={(e) => setSearchQuery(e.target.value)}
                />
              )}

              {/* <SelectMultiple /> */}
              {createRecord && (
                <Button
                  className="btn btn-primary"
                  onClick={() => {
                    setFormData({});
                    setOpenModal(true);
                  }}
                >
                  Create
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    {tableFields?.map((field) => (
                      <th key={field.name}>
                        <strong>{field.label}</strong>
                      </th>
                    ))}
                    {showActions && (
                      <th>
                        <strong>Actions</strong>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.map((rowData, index) => (
                    <tr key={rowData.id}>
                      <td>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      {tableFields.map((field) => (
                        <td key={field.name}>
                          {field.render
                            ? field.render(rowData[field.name])
                            : String(rowData[field.name])}
                        </td>
                      ))}
                      {showActions && (
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="div"
                              className="i-false btn-link btn sharp tp-btn btn-primary pill"
                            >
                              <i className="la la-ellipsis-v" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              {updateRecord && (
                                <Dropdown.Item
                                  onClick={() => {
                                    setFormData(rowData);
                                    setOpenModal(true);
                                  }}
                                >
                                  Edit
                                </Dropdown.Item>
                              )}
                              {deleteRecord && (
                                <Dropdown.Item
                                  onClick={() => handleDeleteConfirm(rowData)}
                                >
                                  Delete
                                </Dropdown.Item>
                              )}
                              {typeof menuOptions === "function"
                                ? menuOptions(rowData).map((item, idx) => (
                                    <Dropdown.Item
                                      key={idx}
                                      onClick={() => item?.click(rowData)}
                                    >
                                      {item?.title}
                                    </Dropdown.Item>
                                  ))
                                : Array.isArray(menuOptions)
                                ? menuOptions.map((item, idx) => (
                                    <Dropdown.Item
                                      key={idx}
                                      onClick={() => item?.click(rowData)}
                                    >
                                      {item?.title}
                                    </Dropdown.Item>
                                  ))
                                : null}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>

              {(!data || data?.length === 0) && (
                <div className="d-flex justify-content-center align-items-center py-2">
                  <h5 className="text-muted">NO DATA FOUND.</h5>
                </div>
              )}

              <div className="d-flex justify-content-center">
                {renderPagination()}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Form
        title={title}
        setOpenModal={setOpenModal}
        openModal={openModal}
        fields={fields?.filter((f) => f.isForm)}
        formData={formData}
        setFormData={setFormData}
        handleFormSubmit={handleFormSubmit}
      />
    </Fragment>
  );
};

export default BootstrapTable;
